import { useState, useRef } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis"
import { GridRows } from "@visx/grid";
import { extent } from "d3-array";
import { scaleBand, scaleLinear } from "@visx/scale";
import { motion } from "framer-motion"
import { alphabet, computerNumberStyles, getTooltipProportionText, remToPx } from "../../assets/constants";
import SvgPlotWrapper from "../svg-plot-wrapper/svg-plot-wrapper.component";
import { useMantineTheme, Text } from "@mantine/core";
import { Group } from "@visx/group";
import { localPoint } from '@visx/event';
import PlotTooltip from "../plot-tooltip/plot-tooltip.component";
import HoverRect from "../hover-rect/hover-rect.component";
import { tooltipTextStyles } from "../../assets/constants";
import { scaleOrdinal } from "@visx/scale";
import { schemeTableau10 } from "d3-scale-chromatic";
import LogoWithText from "../logo-with-text/logo-with-text.component";

const BarPlot = ({
    dimensions,
    surveyResponse,
    isSmall,
    isMedium,
    animateTransition,
    hasMouse,
    plotType,
    clipPathId = 'none',
    hideAxisLabels = false,
    xAxisLabel = 'Response',
    yAxisLabel = 'Proportion'
}) => {

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipOffset, setTooltipOffset] = useState({});
    const [tooltipData, setTooltipData] = useState({ answer: '', proportion: 0 });
    const [barHighlighted, setBarHighlighted] = useState('');

    const theme = useMantineTheme();

    const xScale = scaleBand({
        domain: surveyResponse.surveyAnswers,
        range: [0, dimensions.innerWidth],
        padding: surveyResponse.surveyAnswers.length > 5 ? 0.15 : 0.25
    });

    const colorScale = scaleOrdinal({
        domain: surveyResponse.surveyAnswers,
        range: schemeTableau10
    })

    const xStep = xScale.step();
    const dom = xScale.domain();
    const xPaddingOuter = xScale(dom[0]);

    // Plot setup
    const yExtent = extent(surveyResponse.surveyData.map(d => Object.values(d.responses)).flat());
    const yRangeMin = 0;
    const yRangeMax = plotType === 'dashboard' ? (yExtent[1] + 0.025 * yExtent[1]) : yExtent[1] > 0.9 ? 1.03 : yExtent[1] + 0.05;
    const domain = [yRangeMin, yRangeMax];
    const yScale = scaleLinear({
        domain,
        range: [dimensions.innerHeight, 0]
    });
    const barWidth = xScale.bandwidth();
    const responses = surveyResponse.surveyData[0].responses;

    const longestAnswer = Math.max(...(surveyResponse.surveyAnswers.map(answer => answer.length)));
    const plotLetters = plotType === 'dashboard' ? false : longestAnswer > 12 || isSmall;

    const yTickTranslateAmount = isSmall ? -8 : 0;
    const transitionDuration = animateTransition ? { duration: 0.4 } : { duration: 0 };

    //  Tooltip
    const tooltipRef = useRef(null);
    const tooltipHeight = tooltipRef?.current?.offsetHeight || 0;

    const showTooltipFunction = (event) => {
        const { x } = localPoint(event) || { x: 0 };
        const index = Math.floor((x - (dimensions.margins.left + (xPaddingOuter / 2))) / xStep);
        const indexClamped = Math.max(0, Math.min(index, dom.length - 1));
        const answer = dom[indexClamped];
        const yVal = yScale(responses[answer])
        const barHeight = dimensions.innerHeight - yVal;
        const yOffset = yVal + dimensions.margins.top + (barHeight < (tooltipHeight + 40) ? - (tooltipHeight + 20) : 20);
        setBarHighlighted(answer);
        setTooltipOffset({
            x: xScale(answer) + dimensions.margins.left + (barWidth / 2) - (tooltipWidth / 2),
            y: yOffset,
        })
        setTooltipData({
            answer,
            proportion: responses[answer]
        })
        setTooltipOpen(true);
    };

    const hideTooltipFunction = () => {
        setTooltipOpen(false);
        setBarHighlighted('');
    };

    const tooltipWidth = Math.min(200, barWidth + (xPaddingOuter * 2) + (dimensions.margins.left + dimensions.margins.right) - 20);

    const clipPathIdWithUrl = clipPathId === 'none' ? 'url(#inner-rect)' : `url(#inner-rect-${clipPathId})`;

    return (
        <>
            <SvgPlotWrapper dimensions={dimensions} id = {clipPathId}>
                <GridRows scale={yScale} width={dimensions.innerWidth} height={dimensions.innerHeight} stroke='rgba(255,255,255,0.8)' />
                <LogoWithText innerWidth={dimensions.innerWidth} innerHeight={dimensions.innerHeight} />
                <Group clipPath={clipPathIdWithUrl}>
                    {surveyResponse.surveyAnswers.map(answer => {
                        const yValue = yScale(responses[answer]);
                        const xValue = xScale(answer);
                        const color = colorScale(answer);
                        return (
                            <motion.rect
                                key={answer}
                                x={xValue}
                                width={barWidth}
                                initial={{
                                    height: 0,
                                    y: dimensions.innerHeight,
                                }}
                                animate={{
                                    height: (dimensions.innerHeight - yValue) + 20,
                                    y: yValue,
                                }}
                                ry = {`${remToPx(theme.radius.md)}px`}
                                stroke={barHighlighted === answer ? color : 'none'}
                                strokeWidth={15}
                                strokeOpacity={0.5}
                                transition={transitionDuration}
                                fill={color}
                                opacity='0.8'
                            />
                        )
                    })}
                </Group>
                <AxisLeft
                    scale={yScale}
                    label={(isMedium === false && hideAxisLabels === false) && yAxisLabel}
                    labelOffset={40}
                    numTicks={isSmall ? 0 : 10}
                    hideAxisLine={isSmall}
                    tickLabelProps={() => ({
                        fontSize: 13,
                        textAnchor: 'end',
                        dominantBaseline: 'middle',
                        transform: `translate(-2,0)`
                    })}
                    labelProps={{ style: { fontSize: 16, textAnchor: 'middle', fontWeight: '400' } }}
                />
                <AxisBottom
                    top={dimensions.innerHeight}
                    scale={xScale}
                    label={(isSmall === false && hideAxisLabels === false) && xAxisLabel}
                    labelOffset={25}
                    hideTicks={isSmall}
                    tickFormat={(d, i) => plotLetters ? alphabet[i] : d}
                    tickLabelProps={() => ({
                        fontSize: 13,
                        textAnchor: 'middle',
                        dominantBaseline: 'middle',
                        transform: `translate(0,${yTickTranslateAmount})`,
                        width: dimensions.innerWidth / 5,
                    })}
                    labelProps={{ style: { fontSize: 16, textAnchor: 'middle', fontWeight: '400' } }}
                />
                <HoverRect
                    innerWidth={dimensions.innerWidth}
                    innerHeight={dimensions.innerHeight}
                    hasMouse={hasMouse}
                    showFunction={showTooltipFunction}
                    hideFunction={hideTooltipFunction}
                />
            </SvgPlotWrapper>
            {tooltipOpen &&
                <PlotTooltip
                    tooltipOffset={tooltipOffset}
                    tooltipWidth={tooltipWidth}
                    ref={tooltipRef}
                    plotWidth={dimensions.innerWidth}
                >
                    <Text style={tooltipTextStyles} size={isSmall ? 'xs' : 'sm'} weight={500}>{tooltipData.answer}</Text>
                    <Text size={isSmall ? 'xs' : 'sm'} span>{plotType === 'dashboard' ? 'Count:' : isSmall ? 'Prop:' : 'Proportion:'}&nbsp;</Text>
                    <Text style={computerNumberStyles} size={isSmall ? 'xs' : 'sm'} span>{getTooltipProportionText(tooltipData.proportion,plotType)}</Text>
                </PlotTooltip>
            }
            {
                isSmall === false && plotType !== 'estimated' && 'surveyYears' in surveyResponse &&
                <div
                    style={{
                        position: 'absolute',
                        width: '100px',
                        top: dimensions.margins.top - 32.5,
                        left: dimensions.margins.left + ((dimensions.innerWidth / 2) - 50),
                    }}
                >
                    <Text
                        size='lg'
                        ta="center"
                    >
                        Year: {surveyResponse.surveyYears[0]}
                    </Text>
                </div>
            }
        </>
    )
}

export default BarPlot;